import { useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { useTable, useGlobalFilter } from "react-table";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonSelect from "components/ArgonSelect";
import ArgonInput from "components/ArgonInput";

import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import FilterIcon from "../../../assets/icons/Filter.svg";
import "./DataTable.css";
import { Box, Collapse, TableCell } from "@mui/material";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  noEndBorder,
  setEntriesPerPage,
  setSearch,
  search,
  spinner,
  canFilter,
  showFilter,
  customWidth,
  cellWidth,
  custom,
  nestedRowObject,
  open,
  additionalRow,
  additionalData,
  typeFilter,
  handleFilter,
}) {
  const defaultValue = entriesPerPage;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries
    : [5, 10, 15, 20, 25];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  // Initialize the table instance
  const tableInstance = useTable({ columns, data }, useGlobalFilter);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { globalFilter },
  } = tableInstance;

  return (
    <TableContainer sx={{ boxShadow: "none", overflow: "visible" }}>
      {entriesPerPage || canSearch ? (
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
        >
          {entriesPerPage && !custom && (
            <ArgonBox display="flex" alignItems="center">
              <ArgonBox width="25%">
                <ArgonSelect
                  defaultValue={{ value: defaultValue, label: defaultValue }}
                  options={entries.map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={({ value }) => setEntriesPerPage(value)}
                  size="small"
                />
              </ArgonBox>
              <ArgonTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </ArgonTypography>
            </ArgonBox>
          )}
          {canSearch && (
            <ArgonBox width="12rem" ml="auto">
              <ArgonInput
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </ArgonBox>
          )}
          {typeFilter && handleFilter()}
          {canFilter && (
            <button onClick={showFilter} className="filterButton">
              <img src={FilterIcon} alt="img" />
              &nbsp; Filter
            </button>
          )}
        </ArgonBox>
      ) : null}

      {spinner ? (
        <></>
      ) : (
        <Table {...getTableProps()}>
          <ArgonBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <DataTableHeadCell
                    key={index}
                    {...column.getHeaderProps()}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "space-between"}
                    cellWidth={cellWidth}
                    style={{ color: "#8392ab", textTransform: "none" }}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </ArgonBox>

          {rows.length ? (
            <>
              <TableBody {...getTableBodyProps()}>
                {rows.map((row, key) => {
                  prepareRow(row);
                  return (
                    <TableRow key={key} {...row?.getRowProps()}>
                      {row.cells.map((cell, index) => (
                        <DataTableBodyCell
                          key={index}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={
                            cell.column.align
                              ? cell.column.align
                              : "space-between"
                          }
                          customWidth={customWidth ? true : false}
                          {...cell.getCellProps()}
                          cellWidth={cellWidth}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </>
          ) : (
            <div className="no-records">No Records Found</div>
          )}
        </Table>
      )}

      {additionalRow && <div className="total-vehicle">{additionalData}</div>}

      {showTotalEntries && !custom && (
        <ArgonBox mb={{ xs: 3, sm: 0 }}>
          <ArgonTypography
            variant="button"
            color="secondary"
            fontWeight="regular"
          >
            Showing {1} to {rows.length} of {rows.length} entries
          </ArgonTypography>
        </ArgonBox>
      )}
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  noEndBorder: false,
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
